import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Badge } from '@/components/ui/Badge';
import { EmptyState } from '@/components/ui/EmptyState';
import { Pagination } from '@/components/ui/Pagination';
import {
  kbQueryKeys,
  listKnowledgeBaseCategories,
  listKnowledgeBaseDocuments,
  type KnowledgeBaseDocument,
  type KnowledgeBaseDocumentListParams,
} from '@/services/knowledge-base.service';

const SOURCE_TYPE_LABELS: Record<string, string> = {
  delightree_zapier: 'Delightree',
  zapier: 'Zapier',
  manual: 'Manual',
};

function sourceTypeBadge(sourceType: string) {
  if (sourceType === 'manual') return <Badge variant="info">Manual</Badge>;
  if (sourceType === 'delightree_zapier' || sourceType === 'zapier') {
    return <Badge variant="neutral">{SOURCE_TYPE_LABELS[sourceType] ?? sourceType}</Badge>;
  }
  return <Badge variant="neutral">{sourceType || 'Unknown'}</Badge>;
}

export function LibraryPage() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [category, setCategory] = useState('');
  const [page, setPage] = useState(1);
  const perPage = 25;

  const params: KnowledgeBaseDocumentListParams = useMemo(
    () => ({
      q: q.trim() || undefined,
      category: category || undefined,
      page,
      perPage,
    }),
    [q, category, page],
  );

  const docsQuery = useQuery({
    queryKey: kbQueryKeys.documents(params),
    queryFn: () => listKnowledgeBaseDocuments(params),
  });

  const categoriesQuery = useQuery({
    queryKey: kbQueryKeys.categories(),
    queryFn: listKnowledgeBaseCategories,
    staleTime: 60_000,
  });

  return (
    <div className="grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]">
      <aside className="space-y-2">
        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Categories
        </div>
        <button
          type="button"
          onClick={() => {
            setCategory('');
            setPage(1);
          }}
          className={`block w-full rounded-md px-2 py-1.5 text-left text-sm ${
            category === ''
              ? 'bg-blue-50 font-medium text-blue-700'
              : 'text-slate-700 hover:bg-slate-50'
          }`}
        >
          All categories
        </button>
        {categoriesQuery.data?.map((c) => (
          <button
            key={c.name}
            type="button"
            onClick={() => {
              setCategory(c.name);
              setPage(1);
            }}
            className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-sm ${
              category === c.name
                ? 'bg-blue-50 font-medium text-blue-700'
                : 'text-slate-700 hover:bg-slate-50'
            }`}
          >
            <span className="truncate">{c.name}</span>
            <span className="ml-2 shrink-0 text-xs text-slate-400">{c.count}</span>
          </button>
        ))}
      </aside>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px]">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="Search documents..."
              className="pl-9"
            />
          </div>
          <Link to="new">
            <Button leftIcon={<Plus className="h-4 w-4" />}>New Document</Button>
          </Link>
        </div>

        {docsQuery.isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-6 text-sm text-slate-500">
            Loading documents...
          </div>
        ) : !docsQuery.data?.data.length ? (
          <EmptyState
            icon={<BookOpen className="h-8 w-8" />}
            title="No documents yet"
            description={
              q || category
                ? 'No documents match the current filters.'
                : 'Create a manual document or wait for Zapier to sync content from Delightree.'
            }
            action={{ label: 'New Document', onClick: () => navigate('new') }}
          />
        ) : (
          <>
            <div className="overflow-hidden rounded-lg border border-slate-200 bg-white">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Title
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Category
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Source
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      Updated
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {docsQuery.data.data.map((doc: KnowledgeBaseDocument) => (
                    <tr key={doc.kbDocumentId} className="hover:bg-slate-50">
                      <td className="px-4 py-2.5">
                        <Link
                          to={`${doc.kbDocumentId}`}
                          className="text-sm font-medium text-blue-600 hover:underline"
                        >
                          {doc.title}
                        </Link>
                        {doc.tags.length > 0 && (
                          <div className="mt-0.5 flex flex-wrap gap-1">
                            {doc.tags.slice(0, 4).map((t) => (
                              <span
                                key={t}
                                className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-600"
                              >
                                {t}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-2.5 text-sm text-slate-600">{doc.category || '—'}</td>
                      <td className="px-4 py-2.5">{sourceTypeBadge(doc.sourceType)}</td>
                      <td className="px-4 py-2.5 text-sm text-slate-500">
                        {doc.updatedAt ? new Date(doc.updatedAt).toLocaleDateString() : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              total={docsQuery.data.total}
              pageSize={perPage}
              onPageChange={setPage}
            />
          </>
        )}
      </section>
    </div>
  );
}
